import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Progress,
  Select,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { RiTerminalBoxLine } from 'react-icons/ri';
import { FiAlertCircle, FiInfo } from 'react-icons/fi';
import {
  ConnectionType,
  ConnectorStatus,
  SteeringStatus,
  useGetMyCustomAgentsQuery,
  useTriggerCompactionAnalysisMutation,
  useSteeringRunQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { getSimilarityStyle } from './ProjectTabViewFaq';
import { humanizeTokens } from '../../utils/tokenEstimate';

// Mirrors the worker's STEERING_CANDIDATE_COUNT; ~output tokens for one plan-mode task plan.
const STEER_CANDIDATES = 4;
const PER_TASK_OUTPUT_TOKENS = 1500;

interface CompactionOptimizerProps {
  // The watch's coding agent + the Claude Code session it's pinned to (chosen at creation).
  agentId?: string;
  sessionId?: string | null;
  // The watch's fixed task count (for the cost estimate).
  taskCount?: number;
  // The watch's completed versions, for "steer to a previous version".
  timelineProjects?: Array<{ id: string; dateCreated: string }>;
}

const TERMINAL: SteeringStatus[] = [SteeringStatus.Completed, SteeringStatus.Failed];

export const CompactionOptimizer = ({ agentId, sessionId, taskCount = 0, timelineProjects = [] }: CompactionOptimizerProps) => {
  const toast = useToast();
  // The reference the agent is steered toward: 'latest' (most-recent check-in),
  // 'fresh' (run a new check-in now), or a specific prior version's Project id.
  const [reference, setReference] = useState<string>('latest');
  const updateWatch = reference === 'fresh';
  const referenceVersionId = reference === 'latest' || reference === 'fresh' ? null : reference;

  // Prior versions (newest first), excluding the most recent — that one IS "latest".
  const previousVersions = useMemo(
    () =>
      [...timelineProjects]
        .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
        .slice(1),
    [timelineProjects],
  );

  // Approx monitored-agent output tokens: plan sets × tasks × per-task plan.
  const planSets = STEER_CANDIDATES + (updateWatch ? 2 : 1); // +reference only when fresh
  const estTokens = taskCount > 0 ? planSets * taskCount * PER_TASK_OUTPUT_TOKENS : 0;

  // Connection status of the watch's agent (for the online gate + display name).
  const { data: agentsData } = useGetMyCustomAgentsQuery();
  const agent = useMemo(
    () => (agentsData?.getMyCustomAgents ?? []).find((a) => a.id === agentId),
    [agentsData, agentId],
  );
  const isConnector = agent?.connectionType === ConnectionType.Connector;
  const isOnline = agent?.connectorStatus === ConnectorStatus.Online;

  // Run + polling.
  const [runId, setRunId] = useState<string | undefined>();
  const [trigger, { loading: triggering }] = useTriggerCompactionAnalysisMutation();
  const { data: runData, stopPolling } = useSteeringRunQuery({
    variables: { id: runId as string },
    skip: !runId,
    pollInterval: 3000,
    fetchPolicy: 'network-only',
  });
  const run = runData?.steeringRun;
  useEffect(() => {
    if (run && TERMINAL.includes(run.status)) stopPolling();
  }, [run, stopPolling]);
  const isRunning = !!runId && (!run || !TERMINAL.includes(run.status));

  const onAnalyze = async () => {
    if (!agentId || !sessionId) return;
    setRunId(undefined);
    try {
      const res = await trigger({
        variables: { input: { agentId, sessionId, updateWatch, referenceVersionId } },
      });
      const id = res.data?.triggerCompactionAnalysis.id;
      if (id) setRunId(id);
    } catch (e) {
      toast({ title: 'Could not start analysis', description: String(e), status: 'error', duration: 4000 });
    }
  };

  const copyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    toast({ title: 'Copied — paste & run it', description: command, status: 'success', duration: 2500 });
  };

  // --- Gating ---
  if (!sessionId) {
    return (
      <EmptyState
        title="This watch isn't pinned to a session"
        body="The /compact optimizer runs against the Claude Code session this watch monitors. This watch was created without one — create a new coding watch and pick a session to use it."
      />
    );
  }
  if (!isConnector) {
    return (
      <EmptyState
        title="Not a Claude Code agent"
        body="The /compact optimizer only applies to connector-based coding agents (Claude Code)."
      />
    );
  }

  return (
    <Flex width="full" flexDirection="column" maxWidth="900px" mx="auto" gap={4}>
      {/* Header: the session being optimized + the action */}
      <Flex
        gap={3}
        p={4}
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="12px"
        align="center"
        justify="space-between"
        flexWrap="wrap"
      >
        <HStack spacing={3} minW={0}>
          <Flex w="34px" h="34px" borderRadius="8px" bg={HelivanColor.hover.lightBlue} align="center" justify="center" flexShrink={0}>
            <RiTerminalBoxLine size={16} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <VStack align="start" spacing={0} minW={0}>
            <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
              Recommend a /compact message
            </Text>
            <Text fontSize="12px" color={HelivanColor.text.blueMid} noOfLines={1}>
              For this watch's session · {agent?.name ?? 'agent'}
            </Text>
          </VStack>
        </HStack>
        <VStack align={{ base: 'stretch', sm: 'end' }} spacing={1}>
          <Button
            size="sm"
            colorScheme="blue"
            bg={HelivanColor.text.helivanBlue}
            width={{ base: '100%', sm: 'auto' }}
            isDisabled={!isOnline || triggering || isRunning}
            isLoading={triggering}
            onClick={onAnalyze}
          >
            Optimize
          </Button>
          {estTokens > 0 && (
            <Text fontSize="11px" color={HelivanColor.text.blueMid}>
              ≈ {humanizeTokens(estTokens)} output tokens · {planSets} plan runs (approx, your agent)
            </Text>
          )}
        </VStack>
      </Flex>

      {/* Reference plan — the plan the recommended /compact message should preserve */}
      <VStack
        align="stretch"
        spacing={2}
        px={4}
        py={3}
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="12px"
      >
        <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
          <HStack spacing={1.5}>
            <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
              Reference plan
            </Text>
            <Tooltip
              label="The plan we compare against. We try each candidate /compact message, re-plan the watch's tasks from the compacted context, and recommend the message whose plan stays closest to this reference."
              hasArrow
              placement="top"
            >
              <Box as="span" display="inline-flex" cursor="help">
                <FiInfo size={13} color={HelivanColor.text.blueMid} />
              </Box>
            </Tooltip>
          </HStack>
          <Select
            size="sm"
            width={{ base: '100%', sm: '300px' }}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            isDisabled={isRunning || triggering}
          >
            <option value="latest">Most recent check-in (current)</option>
            <option value="fresh">Re-check the agent now (fresh)</option>
            {previousVersions.length > 0 && (
              <optgroup label="Steer toward a past update">
                {previousVersions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {new Date(v.dateCreated).toLocaleString()}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Flex>
        <Text fontSize="12px" color={HelivanColor.text.blueMid}>
          {reference === 'fresh'
            ? "Re-check the agent now to capture its current plan, then recommend the /compact that best preserves it. Records a new watch version (slower, +1 run)."
            : reference === 'latest'
              ? "Recommend the /compact that best preserves the agent's most recent plan. Fast — reuses the last check-in."
              : "Recommend the /compact that steers the agent back toward this past update's plan. Every completed check-in is listed here."}
        </Text>
      </VStack>

      {agent && !isOnline && (
        <InlineHint text={`"${agent.name}" is offline — start its connector to run the optimizer.`} />
      )}

      {/* Run state */}
      {isRunning && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={12}
          gap={3}
          bg="white"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          borderRadius="12px"
          px={6}
        >
          {!run?.progressTotal ? (
            <Spinner color={HelivanColor.text.helivanBlue} />
          ) : (
            <Box width="full" maxWidth="420px">
              <Progress
                value={(run.progressDone ?? 0) / run.progressTotal * 100}
                size="sm"
                borderRadius="full"
                colorScheme="blue"
                hasStripe
                isAnimated
              />
            </Box>
          )}
          <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
            {run?.progressLabel || 'Compacting & re-planning…'}
          </Text>
          <Text fontSize="13px" color={HelivanColor.text.blueMid} textAlign="center" maxWidth="420px">
            {run?.progressTotal
              ? `Step ${run.progressDone ?? 0} of ${run.progressTotal} · planning the watch's tasks under each candidate.`
              : 'Trying each candidate steering message and measuring how well the agent\'s plan survives.'}
          </Text>
        </Flex>
      )}

      {run?.status === SteeringStatus.Failed && (
        <EmptyState title="Analysis failed" body={run.errorMessage || 'Something went wrong. Please try again.'} />
      )}

      {/* Results */}
      {run?.status === SteeringStatus.Completed && run.results && (
        <VStack align="stretch" spacing={3}>
          <Text fontSize="13px" color={HelivanColor.text.blueMid}>
            Ranked by how closely each compaction's plan matches the full-context plan. Higher = the steering
            message preserved more of what the agent intended. Copy the top one and run it as your /compact.
          </Text>
          {run.results.candidates.map((c, idx) => {
            const sim = getSimilarityStyle(c.similarity);
            const isRecommended = idx === 0 && (c.similarity ?? -1) >= 0;
            return (
              <Box
                key={idx}
                bg="white"
                border="1px solid"
                borderColor={isRecommended ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                borderLeft="4px solid"
                borderLeftColor={sim.border}
                borderRadius="12px"
                p={4}
                boxShadow={isRecommended ? '0 1px 6px rgba(0,0,0,0.08)' : 'none'}
              >
                {(() => {
                  const command = c.message ? `/compact ${c.message}` : '/compact';
                  return (
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between" align="center">
                        <HStack spacing={2}>
                          {isRecommended && (
                            <Badge colorScheme="blue" fontSize="10px">
                              Recommended
                            </Badge>
                          )}
                          {c.isBaseline && (
                            <Badge colorScheme="gray" fontSize="10px">
                              No steering message
                            </Badge>
                          )}
                        </HStack>
                        <Button
                          size="xs"
                          variant="outline"
                          leftIcon={<MdContentCopy size={13} />}
                          onClick={() => copyCommand(command)}
                          flexShrink={0}
                        >
                          Copy
                        </Button>
                      </HStack>
                      {/* The full command — copy & paste as-is to run it */}
                      <Box
                        as="pre"
                        onClick={() => copyCommand(command)}
                        cursor="pointer"
                        title="Click to copy"
                        width="full"
                        bg="#0f172a"
                        color="#e2e8f0"
                        fontFamily="mono"
                        fontSize="13px"
                        lineHeight="1.6"
                        px={3}
                        py={2.5}
                        borderRadius="8px"
                        whiteSpace="pre-wrap"
                        overflowWrap="anywhere"
                      >
                        {command}
                      </Box>
                      {/* Similarity bar */}
                      <Flex align="center" gap={2} width="full">
                        <Box flex={1} h="6px" bg={HelivanColor.background.secondary} borderRadius="full" overflow="hidden">
                          <Box
                            h="full"
                            width={`${Math.round((c.similarity ?? 0) * 100)}%`}
                            bg={sim.border}
                            borderRadius="full"
                          />
                        </Box>
                        <Tooltip label="Cosine similarity of this plan to the full-context plan" hasArrow placement="top">
                          <Badge colorScheme={sim.scheme} fontSize="11px" minWidth="84px" textAlign="center">
                            {sim.label}
                          </Badge>
                        </Tooltip>
                      </Flex>
                    </VStack>
                  );
                })()}
              </Box>
            );
          })}
        </VStack>
      )}
    </Flex>
  );
};

const EmptyState = ({ title, body }: { title: string; body: string }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    textAlign="center"
    py={16}
    gap={2}
    bg="white"
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    borderRadius="12px"
  >
    <Icon as={FiAlertCircle} boxSize="26px" color={HelivanColor.text.blueMid} />
    <Text fontSize="16px" fontWeight={700} color={HelivanColor.text.blueDark}>
      {title}
    </Text>
    <Text fontSize="14px" color={HelivanColor.text.blueMid} maxWidth="440px" lineHeight="1.6">
      {body}
    </Text>
  </Flex>
);

const InlineHint = ({ text }: { text: string }) => (
  <HStack
    spacing={2}
    px={3}
    py={2}
    bg={HelivanColor.background.secondary}
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    borderRadius="8px"
  >
    <FiAlertCircle size={14} color={HelivanColor.text.blueMid} />
    <Text fontSize="13px" color={HelivanColor.text.blueMid}>
      {text}
    </Text>
  </HStack>
);
